import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { SECTORS } from "@/data/sectors";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, Check, Sparkles, FileText, GraduationCap, ClipboardCheck, PhoneCall, BookOpen, Tag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import NewsletterSignup from "@/components/NewsletterSignup";
import { Price } from "@/components/Price";
import IfaChecklistDownload from "@/components/IfaChecklistDownload";
import PackFlickthrough from "@/components/PackFlickthrough";
import { FoundersBanner, SECTOR_TO_SPECIALITY } from "@/components/FoundersClub";

const HELPS = [
  { icon: Sparkles, title: "AI assistant + specialist", body: "Instant answers, and when a question needs judgement it's routed straight to our compliance specialist." },
  { icon: FileText, title: "Policies that fit", body: "Starter and complete packs mapped to your regulator, delivered as ready-to-brand PDFs." },
  { icon: GraduationCap, title: "Mock fit-person interview", body: "Six inspector-style questions, scored feedback and a written readiness report." },
  { icon: ClipboardCheck, title: "Weekly readiness checklist", body: "A sector-specific checklist, training tracker and readiness score on your dashboard." },
];

function useSectorData(sector) {
  const [products, setProducts] = useState([]);
  const [articles, setArticles] = useState([]);
  useEffect(() => {
    api.get("/resources").then(({ data }) => setProducts(sector.products.map((k) => data.find((r) => r.lookup_key === k)).filter(Boolean))).catch((e) => logError("resources", e));
    api.get("/articles").then(({ data }) => setArticles(data.filter((a) => sector.articleCategories.includes(a.category)).slice(0, 3))).catch((e) => logError("articles", e));
  }, [sector]);
  return { products, articles };
}

function FaqSchema({ sector }) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: sector.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) });
    document.head.appendChild(el);
    return () => el.remove();
  }, [sector]);
  return null;
}

function Hero({ sector, user, fromPrice }) {
  const Icon = sector.icon;
  return (
    <section className={`bg-gradient-to-br ${sector.accent} text-white`} data-testid="sector-hero">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-white/80 mb-5"><Icon className="w-4 h-4" /> {sector.tag}</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] mb-5" data-testid="sector-headline">{sector.headline}</h1>
          <p className="text-lg text-white/90 leading-relaxed max-w-2xl mb-5">{sector.intro}</p>
          {fromPrice != null && (
            <a href="#sector-products" className="inline-flex items-center gap-2 rounded-full bg-white/15 border border-white/30 px-4 py-1.5 text-sm font-medium mb-6 hover:bg-white/25 transition-colors" data-testid="sector-from-price">
              <Tag className="w-4 h-4" /> Inspection-ready policy packs from <Price value={fromPrice} className="font-bold" /> · Word + PDF, editable in-app
            </a>
          )}
          <div className="flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-white text-foreground hover:bg-white/90" data-testid="sector-cta-primary">
              <Link to={user ? "/dashboard" : "/register"}>{user ? "Go to dashboard" : "Start free"} <ArrowRight className="w-4 h-4 ml-1.5" /></Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white" data-testid="sector-cta-ai">
              <Link to={`/assistant?sector=${sector.id}&q=${encodeURIComponent(sector.aiPrompt)}`}><Sparkles className="w-4 h-4 mr-1.5" /> Ask the assistant</Link>
            </Button>
          </div>
        </div>
        <div className="lg:col-span-5 bg-white/10 backdrop-blur rounded-2xl p-6 border border-white/20" data-testid="sector-audiences">
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-white/80 mb-3">Who it's for</p>
          <ul className="space-y-2.5">
            {sector.audiences.map((a) => <li key={a} className="flex items-start gap-2 text-sm"><Check className="w-4 h-4 mt-0.5 shrink-0" /> {a}</li>)}
          </ul>
        </div>
      </div>
    </section>
  );
}

function Products({ products, sectorId }) {
  if (!products.length) return null;
  return (
    <section id="sector-products" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 scroll-mt-24" data-testid="sector-products">
      <span className="overline text-accent">Resources</span>
      <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1 mb-6">Built for your sector</h2>
      <div className="mb-8"><FoundersBanner speciality={SECTOR_TO_SPECIALITY[sectorId]} compact /></div>
      <div className="grid lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-5">
          <PackFlickthrough sectorId={sectorId} share />
          <p className="text-xs text-muted-foreground mt-3" data-testid="sector-pack-video-note">Real pages from the pack — every policy is editable in-app after purchase.</p>
        </div>
        <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
          {products.map((p) => (
            <Link key={p.id} to="/resources" className="rounded-2xl border border-border bg-card p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex flex-col" data-testid={`sector-product-${p.lookup_key}`}>
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{p.category}</span>
              <h3 className="font-display font-semibold mt-1 mb-2 leading-snug">{p.title}</h3>
              <p className="text-sm text-muted-foreground flex-1 line-clamp-3">{p.description}</p>
              <div className="mt-4"><Price value={p.price_gbp} compact className="font-display text-xl font-extrabold" testId={`sector-price-${p.lookup_key}`} /></div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Sector({ sectorId }) {
  const { user } = useAuth();
  const sector = SECTORS[sectorId];
  const { products, articles } = useSectorData(sector);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash && products.length) document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "smooth" });
  }, [hash, products.length]);
  const packPrices = products.filter((p) => p.type === "policy" && p.price_gbp != null).map((p) => p.price_gbp);
  const fromPrice = packPrices.length ? Math.min(...packPrices) : null;

  return (
    <div data-testid={`sector-page-${sector.id}`}>
      <FaqSchema sector={sector} />
      <Hero sector={sector} user={user} fromPrice={fromPrice} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <span className="overline text-accent">The challenge</span>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1 mb-8">What keeps managers awake</h2>
        <div className="grid md:grid-cols-3 gap-6" data-testid="sector-challenges">
          {sector.challenges.map((c) => (
            <div key={c.title} className="rounded-2xl border border-border bg-card p-6 shadow-sm">
              <h3 className="font-display font-semibold text-lg mb-2">{c.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-secondary/40 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <span className="overline text-accent">How we help</span>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1 mb-8">One platform, a specialist on standby</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="sector-helps">
            {HELPS.map((h) => (
              <div key={h.title} className="flex gap-4">
                <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><h.icon className="w-5 h-5 text-primary" /></div>
                <div><h3 className="font-semibold mb-1">{h.title}</h3><p className="text-sm text-muted-foreground leading-relaxed">{h.body}</p></div>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild variant="outline" data-testid="sector-startup-link"><Link to={`/start-up?track=${sector.startupTrack}`}>Registration roadmap <ArrowRight className="w-4 h-4 ml-1.5" /></Link></Button>
            <Button asChild variant="outline" data-testid="sector-pricing-link"><Link to="/pricing">See subscription plans</Link></Button>
          </div>
        </div>
      </section>

      <Products products={products} sectorId={sector.id} />

      {articles.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="sector-articles">
          <span className="overline text-accent">Guidance</span>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1 mb-8">Read before you register</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {articles.map((a) => (
              <Link key={a.slug} to={`/guidance/${a.slug}`} className="rounded-2xl border border-border bg-card p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col" data-testid={`sector-article-${a.slug}`}>
                <span className="text-[11px] font-bold uppercase tracking-wider text-accent inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> {a.category}</span>
                <h3 className="font-display font-semibold mt-2 mb-2 leading-snug">{a.title}</h3>
                <p className="text-sm text-muted-foreground flex-1 line-clamp-3">{a.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {sector.id === "fostering" && <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"><IfaChecklistDownload /></section>}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16"><NewsletterSignup source={`sector-${sector.id}`} defaultRegulator={sector.regulator} /></section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="sector-faqs">
        <span className="overline text-accent">FAQs</span>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1 mb-6">Common questions</h2>
        <Accordion type="single" collapsible className="w-full">
          {sector.faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`sector-faq-${i}`}>
              <AccordionTrigger className="text-left font-semibold">{f.q}</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className={`rounded-3xl bg-gradient-to-r ${sector.accent} text-white p-10 lg:p-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6`} data-testid="sector-cta-band">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">Talk to a specialist</h2>
            <p className="text-white/90 max-w-xl">Not sure where to start? Ask the assistant or call our compliance specialist on <a href="tel:01135222861" className="font-semibold underline underline-offset-2">0113 522 2861</a>.</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button asChild size="lg" className="bg-white text-foreground hover:bg-white/90"><Link to={user ? "/dashboard" : "/register"}>{user ? "Go to dashboard" : "Create your free account"}</Link></Button>
            <Button asChild size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 hover:text-white"><a href="tel:01135222861"><PhoneCall className="w-4 h-4 mr-1.5" /> Call now</a></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
